import { useQuery } from '@tanstack/react-query';
import api from '@/lib/api';
import { useDynamicFavicon } from './useDynamicFavicon';
import { toRelativeUploadUrl } from '@/lib/utils';

interface PlatformBrandingData {
  logo_url: string | null;
  app_name: string;
  theme_color: string;
}

/**
 * Derive the 64px favicon variant URL from the 512px logo URL.
 * The branding upload pipeline generates both variants with the same hash prefix.
 */
function getFaviconUrl(logoUrl: string | null | undefined): string | null {
  if (!logoUrl) return null;
  const relativeLogo = toRelativeUploadUrl(logoUrl) || logoUrl;
  return relativeLogo.replace(/-512\.webp$/, '-64.webp');
}

/**
 * Fetch public platform branding (no auth required).
 * Provides logo URL, app name, theme color, and optionally sets the dynamic favicon.
 *
 * @param options.setFavicon - If true, automatically sets the page favicon from the platform logo (default: true)
 */
export function usePlatformBranding(options?: { setFavicon?: boolean }) {
  const setFavicon = options?.setFavicon !== false;

  const { data, isLoading } = useQuery({
    queryKey: ['public-platform-branding'],
    queryFn: () =>
      api.get<{ success: boolean; data: PlatformBrandingData }>('/public/platform-branding'),
    staleTime: 60 * 60 * 1000, // 1 hour
    retry: 1,
  });

  const branding = data?.data?.data;
  const logoUrl = branding?.logo_url ?? null;
  const appName = branding?.app_name || 'AMuster';
  const themeColor = branding?.theme_color || '#1e40af';

  useDynamicFavicon(setFavicon ? getFaviconUrl(logoUrl) : null);

  return { logoUrl, appName, themeColor, isLoading };
}
